import { Spot } from '../types/spot';

/**
 * Dynamic OpenStreetMap / Overpass API Query Service
 * Allows scanning live nearby tourist attractions, viewpoints, artwork, and historic POIs in Korea.
 */

interface OverpassElement {
  id: number;
  lat?: number;
  lon?: number;
  center?: { lat: number; lon: number };
  tags?: {
    name?: string;
    'name:en'?: string;
    'name:ko'?: string;
    'name:de'?: string;
    tourism?: string;
    historic?: string;
    viewpoint?: string;
    artwork_type?: string;
    description?: string;
    wikipedia?: string;
    website?: string;
    leisure?: string;
    sport?: string;
    activity?: string;
    [key: string]: string | undefined;
  };
}

export async function fetchLiveNearbyPois(
  lat: number,
  lng: number,
  radiusMeters: number = 5000
): Promise<Spot[]> {
  // Clamp radius between 1000m and 15000m
  const safeRadius = Math.min(15000, Math.max(1000, Math.round(radiusMeters)));

  // Bounded query for tourism attractions, viewpoints, historic landmarks, artwork, museums
  const query = `
    [out:json][timeout:8];
    (
      node["tourism"~"attraction|viewpoint|artwork|theme_park|zoo|aquarium|gallery|museum"](around:${safeRadius},${lat},${lng});
      node["historic"~"monument|memorial|castle|palace|temple|ruins|fort"](around:${safeRadius},${lat},${lng});
      way["tourism"~"attraction|viewpoint|theme_park|zoo"](around:${safeRadius},${lat},${lng});
      way["historic"~"castle|palace|temple|fort"](around:${safeRadius},${lat},${lng});
    );
    out center 35;
  `;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 7000);

  try {
    const response = await fetch('https://overpass-api.de/api/interpreter', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: `data=${encodeURIComponent(query)}`,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      return [];
    }

    const data = await response.json();
    const elements: OverpassElement[] = data.elements || [];

    const spots: Spot[] = elements
      .filter((el) => {
        const spotLat = el.lat || el.center?.lat;
        const spotLng = el.lon || el.center?.lon;
        const name = el.tags?.name || el.tags?.['name:en'] || el.tags?.['name:ko'];
        return spotLat && spotLng && name;
      })
      .map((el) => {
        const spotLat = el.lat || el.center!.lat;
        const spotLng = el.lon || el.center!.lon;
        const nameKo = el.tags?.['name:ko'] || el.tags?.name || '관광지';
        const nameEn = el.tags?.['name:en'] || el.tags?.name || nameKo;
        const nameDe = el.tags?.['name:de'] || nameEn;

        let category: Spot['category'] = 'photo_spot';
        if (el.tags?.leisure || el.tags?.sport || el.tags?.activity) category = 'activity';
        else if (el.tags?.historic || el.tags?.tourism === 'museum') category = 'palace_heritage';
        else if (el.tags?.tourism === 'viewpoint') category = 'nature_viewpoint';
        else if (el.tags?.tourism === 'artwork') category = 'photo_spot';

        return {
          id: `osm-${el.id}`,
          name: nameEn,
          nameDe: nameDe,
          nameKr: nameKo,
          category,
          region: 'Other',
          district: 'Live Spot',
          lat: spotLat,
          lng: spotLng,
          rating: 4.7,
          reviewCount: 45,
          description: el.tags?.description || `Sehenswürdigkeit via OpenStreetMap in deiner Nähe.`,
          descriptionDe: el.tags?.description || `Sehenswürdigkeit via OpenStreetMap in deiner Nähe.`,
          photoTips: `Achte auf besondere Blickwinkel und Fotoperspektiven vor Ort.`,
          photoTipsDe: `Achte auf besondere Blickwinkel und Fotoperspektiven vor Ort.`,
          bestTime: 'Tagesüber',
          bestTimeDe: 'Tagsüber',
          imageUrl: 'https://images.unsplash.com/photo-1538669715315-155098f6da41?w=800&auto=format&fit=crop&q=80',
          subwayInfo: `GPS (${spotLat.toFixed(3)}, ${spotLng.toFixed(3)})`,
          feeInfo: 'Öffentlich zugänglich',
          feeInfoDe: 'Öffentlich zugänglich',
          tags: ['Live Nearby', el.tags?.tourism || el.tags?.historic || 'Spot'],
          isMustSee: false,
        };
      });

    return spots;
  } catch {
    clearTimeout(timeoutId);
    return [];
  }
}
